import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './LLD.css';

export default function LLDDocument() {
  const [documentData, setDocumentData] = useState(null);
  const loc = useLocation();

  const urlParams = new URLSearchParams(loc.search);
  let problemId = urlParams.get('problemId');
  if (!problemId) {
    const pathParts = loc.pathname.split('/');
    problemId = pathParts[2];
  }
  if (!problemId) {
    problemId = localStorage.getItem('current-lld-problemId');
  }

  const loadFromStorage = () => {
    if (!problemId) return;
    const raw = localStorage.getItem(`lld-attempt-${problemId}`);
    if (raw) {
      try { setDocumentData(JSON.parse(raw)); } catch (e) {}
    }
  };

  useEffect(() => {
    loadFromStorage();

    // Refresh when diagrams save a snapshot
    const handleSnapshotUpdate = (e) => {
      if (!e.detail?.problemId || e.detail.problemId === problemId) {
        loadFromStorage();
      }
    };
    window.addEventListener('lldDiagramSnapshotUpdated', handleSnapshotUpdate);

    // Refresh on generic localStorageUpdated event (legacy)
    const handleLocalStorage = (e) => {
      if (!e.detail?.key || e.detail.key === `lld-attempt-${problemId}`) {
        loadFromStorage();
      }
    };
    window.addEventListener('localStorageUpdated', handleLocalStorage);

    // Real-time update: fires immediately whenever any component writes lld-attempt-${problemId}
    const handleAttemptUpdated = (e) => {
      if (e.detail?.key === `lld-attempt-${problemId}`) loadFromStorage();
    };
    window.addEventListener('attemptDataUpdated', handleAttemptUpdated);

    return () => {
      window.removeEventListener('lldDiagramSnapshotUpdated', handleSnapshotUpdate);
      window.removeEventListener('localStorageUpdated', handleLocalStorage);
      window.removeEventListener('attemptDataUpdated', handleAttemptUpdated);
    };
  }, [problemId]);

  if (!documentData) {
    return <div className="lld-document-empty" style={{ padding: '20px' }}>No document data available</div>;
  }

  // ── helpers ──────────────────────────────────────────────────────────────
  const formatFeatures = (features) => {
    if (!features || !Array.isArray(features)) return [];
    return features
      .map(f => {
        if (typeof f === 'string' && f.trim()) return f.trim();
        if (f && typeof f === 'object') {
          if (f.evaluationStatus === 'correct' && f.userInput?.trim()) return f.userInput.trim();
          if (f.text?.trim()) return f.text.trim();
        }
        return null;
      })
      .filter(Boolean);
  };

  const formatDesignPatterns = (patterns) => {
    if (!patterns || !Array.isArray(patterns) || patterns.length === 0) return [];
    return patterns.map(p => ({ name: p.pattern || 'Unnamed Pattern', usage: p.usage || '' }));
  };

  const features = formatFeatures(documentData.features);
  const patterns = formatDesignPatterns(documentData.designPatterns);
  const schemaSnapshot = documentData.schemaSnapshot;
  const classSnapshot  = documentData.classSnapshot;

  return (
    <div className="lld-document-content">

      {/* 1. Features */}
      <div className="lld-document-section">
        <h3>1. Features &amp; Requirements</h3>
        {features.length > 0 ? (
          <ol className="lld-document-list">
            {features.map((f, i) => <li key={i}>{f}</li>)}
          </ol>
        ) : (
          <p className="lld-document-empty">No features defined yet</p>
        )}
      </div>

      {/* 2. Diagram */}
      <div className="lld-document-section">
        <h3>2. Diagram</h3>
        {schemaSnapshot || classSnapshot ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {schemaSnapshot && (
              <div>
                <p style={{ margin: '0 0 6px', fontSize: '0.8rem', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Entities / Schema
                </p>
                <img
                  src={schemaSnapshot}
                  alt="Schema diagram"
                  style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#fff' }}
                />
              </div>
            )}
            {classSnapshot && (
              <div>
                <p style={{ margin: '0 0 6px', fontSize: '0.8rem', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Class Diagram
                </p>
                <img
                  src={classSnapshot}
                  alt="Class diagram"
                  style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#fff' }}
                />
              </div>
            )}
          </div>
        ) : (
          <p className="lld-document-empty">Save the diagram to see a snapshot here</p>
        )}
      </div>

      {/* 3. Design Patterns */}
      <div className="lld-document-section">
        <h3>3. Design Patterns</h3>
        {patterns.length > 0 ? (
          <div className="lld-document-patterns">
            {patterns.map((p, i) => (
              <div key={i} className="lld-document-pattern">
                <h4>{p.name}</h4>
                {p.usage && (
                  <div className="lld-document-pattern-requirement">
                    <strong>Applied:</strong> {p.usage}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="lld-document-empty">No design patterns selected yet</p>
        )}
      </div>

    </div>
  );
}
